/**
 * Compliance Matrix View Component
 * Displays compliance matrix with filtering, sorting, and status updates
 * Addresses Gap 6 from gap analysis
 */

import React, { useState, useMemo } from 'react';
import { Card, Button, Badge, Input, Select } from '@tracekit/core';
import { CheckCircle, XCircle, AlertCircle, FileText, Download, Search, Filter } from 'lucide-react';
import type { ComplianceMatrixRow, ComplianceMatrix } from '../types/template';

interface ComplianceMatrixViewProps {
  complianceMatrix: ComplianceMatrix;
  onStatusUpdate?: (criteriaId: string, newStatus: ComplianceMatrixRow['status']) => void;
  readOnly?: boolean;
}

export function ComplianceMatrixView({
  complianceMatrix,
  onStatusUpdate,
  readOnly = false,
}: ComplianceMatrixViewProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<keyof ComplianceMatrixRow>('criteria_id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Parse compliance matrix (handle both string and array formats)
  const matrixRows = useMemo((): ComplianceMatrixRow[] => {
    if (typeof complianceMatrix === 'string') {
      // Parse Markdown table string
      return parseMarkdownTable(complianceMatrix);
    }
    return complianceMatrix || [];
  }, [complianceMatrix]);

  // Filter and sort rows
  const filteredAndSortedRows = useMemo(() => {
    let rows = [...matrixRows];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      rows = rows.filter(row =>
        row.criteria_id.toLowerCase().includes(term) ||
        row.requirement.toLowerCase().includes(term) ||
        row.proposal_section.toLowerCase().includes(term)
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      rows = rows.filter(row => row.status === filterStatus);
    }

    // Apply sorting
    rows.sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return rows;
  }, [matrixRows, searchTerm, filterStatus, sortBy, sortDirection]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = matrixRows.length;
    const ready = matrixRows.filter(r => r.status === 'ready').length;
    const draft = matrixRows.filter(r => r.status === 'draft').length;
    const missing = matrixRows.filter(r => r.status === 'missing').length;
    const pending = matrixRows.filter(r => r.status === 'pending').length;
    
    return { total, ready, draft, missing, pending, completion: total > 0 ? (ready / total) * 100 : 0 };
  }, [matrixRows]);

  const handleStatusUpdate = (criteriaId: string, newStatus: ComplianceMatrixRow['status']) => {
    if (readOnly || !onStatusUpdate) return;
    onStatusUpdate(criteriaId, newStatus);
  };

  const handleSort = (column: keyof ComplianceMatrixRow) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const exportToCSV = () => {
    const headers = ['Criteria ID', 'Requirement', 'Proposal Section', 'Page(s)', 'Evidence Required', 'Status', 'Notes'];
    const rows = filteredAndSortedRows.map(row => [
      row.criteria_id,
      row.requirement,
      row.proposal_section,
      row.page_reference,
      row.evidence_required,
      row.status,
      row.notes || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'compliance_matrix.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status: ComplianceMatrixRow['status']) => {
    const config = {
      ready: { color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400', icon: CheckCircle, label: 'Ready' },
      draft: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400', icon: FileText, label: 'Draft' },
      missing: { color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400', icon: XCircle, label: 'Missing' },
      pending: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400', icon: AlertCircle, label: 'Pending' },
    };

    const { color, icon: Icon, label } = config[status] || config.pending;
    return (
      <Badge className={`inline-flex items-center gap-1 ${color}`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  if (!matrixRows || matrixRows.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <p className="text-gray-600 dark:text-theme-text-secondary">
            No compliance matrix available. Generate a template first.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      {/* Header with stats */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text">
            Compliance Matrix
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Track criterion compliance and page references
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Badge variant="success">{stats.ready} Ready</Badge>
            <Badge variant="warning">{stats.draft} Draft</Badge>
            <Badge variant="error">{stats.missing} Missing</Badge>
            <Badge variant="default">{stats.pending} Pending</Badge>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportToCSV}
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Completion Progress
          </span>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {stats.completion.toFixed(0)}%
          </span>
        </div>
        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 transition-all duration-300"
            style={{ width: `${stats.completion}%` }}
          />
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search criteria, requirements, sections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="w-40"
        >
          <option value="all">All Status</option>
          <option value="ready">Ready</option>
          <option value="draft">Draft</option>
          <option value="missing">Missing</option>
          <option value="pending">Pending</option>
        </Select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => handleSort('criteria_id')}
              >
                Criteria ID {sortBy === 'criteria_id' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => handleSort('requirement')}
              >
                Requirement {sortBy === 'requirement' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => handleSort('proposal_section')}
              >
                Proposal Section {sortBy === 'proposal_section' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Page(s)
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Evidence Required
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => handleSort('status')}
              >
                Status {sortBy === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              {!readOnly && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredAndSortedRows.map((row, idx) => (
              <tr
                key={row.criteria_id || idx}
                className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors"
              >
                <td className="px-4 py-3">
                  <span className="font-mono text-sm font-medium text-gray-900 dark:text-theme-text">
                    {row.criteria_id}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-gray-900 dark:text-theme-text max-w-md">
                    {row.requirement}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    {row.proposal_section}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Input
                    type="text"
                    value={row.page_reference}
                    placeholder="Page X"
                    className="w-24 text-sm"
                    readOnly={readOnly}
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-gray-700 dark:text-gray-300 max-w-xs">
                    {row.evidence_required}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {getStatusBadge(row.status)}
                </td>
                {!readOnly && (
                  <td className="px-4 py-3">
                    <Select
                      value={row.status}
                      onChange={(e) => handleStatusUpdate(row.criteria_id, e.target.value as ComplianceMatrixRow['status'])}
                      className="w-32 text-sm"
                    >
                      <option value="pending">Pending</option>
                      <option value="draft">Draft</option>
                      <option value="ready">Ready</option>
                      <option value="missing">Missing</option>
                    </Select>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer with count */}
      <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
        Showing {filteredAndSortedRows.length} of {matrixRows.length} criteria
      </div>
    </Card>
  );
}

/**
 * Parse Markdown table string into ComplianceMatrixRow array
 */
function parseMarkdownTable(markdown: string): ComplianceMatrixRow[] {
  const rows: ComplianceMatrixRow[] = [];
  const lines = markdown.split('\n').filter(line => line.trim());

  // Find header row (starts with |)
  let headerIndex = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trim().startsWith('|') && lines[i].includes('Criteria')) {
      headerIndex = i;
      break;
    }
  }

  if (headerIndex === -1) return rows;

  // Parse data rows (skip header and separator)
  for (let i = headerIndex + 2; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line.startsWith('|')) continue;

    const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell);
    if (cells.length < 6) continue;

    rows.push({
      criteria_id: cells[0],
      requirement: cells[1],
      proposal_section: cells[2],
      page_reference: cells[3],
      evidence_required: cells[4],
      status: parseStatus(cells[5]),
      notes: cells.length > 6 ? cells[6] : undefined
    });
  }

  return rows;
}

/**
 * Parse status string from Markdown table
 */
function parseStatus(statusCell: string): ComplianceMatrixRow['status'] {
  const lower = statusCell.toLowerCase();
  if (lower.includes('ready') || lower.includes('[x]')) return 'ready';
  if (lower.includes('draft')) return 'draft';
  if (lower.includes('missing')) return 'missing';
  return 'pending';
}
